import * as THREE from "three";
import { MaterialTypes } from "../globals";

const tempVector2 = new THREE.Vector2();

class LineMultipassMaterial extends THREE.ShaderMaterial
{
	onMultipassRender: (camera: THREE.Camera, scene: THREE.Scene, geometry: THREE.BufferGeometry, renderer: THREE.WebGLRenderer, object: THREE.Object3D, group: any) => void;

	constructor()
	{
		super({
			transparent: true,
			blending: THREE.CustomBlending,
			blendEquation: THREE.AddEquation,
			blendSrc: THREE.SrcAlphaFactor,
			blendDst: THREE.OneMinusSrcAlphaFactor,
			side: THREE.BackSide,
			uniforms: {
				u_lineColor: new THREE.Uniform(new THREE.Color(1, 1, 1)),
				u_reciprocalResolution: { value: new THREE.Vector2()},
				u_lineThickness: { value: 3.0 },
				u_alpha: { value: 1.0 },
			},

			vertexShader: [

				THREE.ShaderChunk["morphtarget_pars_vertex"],
				THREE.ShaderChunk["skinning_pars_vertex"],

				"uniform float u_lineThickness;",
				"uniform vec2 u_reciprocalResolution;",

				"void main()",
				"{",
				THREE.ShaderChunk["skinbase_vertex"],
				THREE.ShaderChunk["begin_vertex"],
				THREE.ShaderChunk["morphtarget_vertex"],
				THREE.ShaderChunk["skinning_vertex"],

				"vec4 mvpPosition = projectionMatrix * modelViewMatrix * vec4(transformed, 1.0);",
				"vec3 clipSpaceNormal = mat3(projectionMatrix) * mat3(modelViewMatrix) * normal;",
				"vec2 offset = normalize(clipSpaceNormal.xy) * u_lineThickness * mvpPosition.w * 2.0 * u_reciprocalResolution;",
				"mvpPosition.xy += offset;",
				"gl_Position = mvpPosition;",

				"}"
			].join("\n"),

			fragmentShader: [
				"uniform vec3 u_lineColor;",
				"uniform float u_alpha;",
				"void main()",
				"{",
				"   gl_FragColor = vec4(u_lineColor, u_alpha);",
				"	gl_FragColor = linearToOutputTexel( gl_FragColor );",
				"}"
			].join("\n")

		});

		this.name = 'lineMultipassMaterial';
		this.onMultipassRender = function(camera: THREE.Camera, scene: THREE.Scene, geometry: THREE.BufferGeometry, renderer: THREE.WebGLRenderer, object: THREE.Object3D, group: any) 
		{
			renderer.getSize(tempVector2);
			this.uniforms["u_reciprocalResolution"].value.x = 1.0 / (tempVector2.x / window.devicePixelRatio);
			this.uniforms["u_reciprocalResolution"].value.y = 1.0 / (tempVector2.y / window.devicePixelRatio);
			renderer.renderBufferDirect( camera, scene, geometry, this, object, group );
		}
	}
	
	get alpha()
	{
		return this.uniforms['u_alpha'].value;
	}
	set alpha(alpha)
	{
		this.uniforms['u_alpha'].value = alpha;
	}

	get lineColor()
	{
		return this.uniforms['u_lineColor'].value;
	}

	set lineColor(color)
	{
		this.uniforms['u_lineColor'].value.copy(color);
	}

	get lineThickness()
	{
		return this.uniforms['u_lineThickness'].value;
	}

	set lineThickness(thickness)
	{
		this.uniforms['u_lineThickness'].value = thickness;
	}
	
	copy(source: LineMultipassMaterial)
	{
		super.copy(source);
		this.lineColor = source.lineColor;
		this.lineThickness = source.lineThickness;
		return this;
	}
}

export class BaseLineMaterial extends THREE.ShaderMaterial
{
	multipassMaterials: Array<LineMultipassMaterial>;
	isCustomExportMaterial: boolean;
	requiresMultipassRendering: boolean;

	constructor()
	{
		super({
			transparent: true,
			blending: THREE.CustomBlending,
			blendEquation: THREE.AddEquation,
			blendSrc: THREE.ZeroFactor,
			blendDst: THREE.OneFactor,
			side: THREE.FrontSide,

			vertexShader: [

				THREE.ShaderChunk["morphtarget_pars_vertex"],
				THREE.ShaderChunk["skinning_pars_vertex"],

				"void main()",
				"{",
				THREE.ShaderChunk["skinbase_vertex"],
				THREE.ShaderChunk["begin_vertex"],
				THREE.ShaderChunk["morphtarget_vertex"],
				THREE.ShaderChunk["skinning_vertex"],

				"gl_Position = projectionMatrix * modelViewMatrix * vec4(transformed, 1.0);",
				"}"
			].join("\n"),

			fragmentShader: [
				"void main()",
				"{",
				"    gl_FragColor = vec4(0.0);",
				"}"
			].join("\n")

		});

		this.name = 'baseLineMat';
		this.isCustomExportMaterial = true;
		this.requiresMultipassRendering = true;	
		this.multipassMaterials = [new LineMultipassMaterial()];
	}

	get alpha()
	{
		return this.multipassMaterials[0].alpha;
	}
	set alpha(alpha)
	{
		this.multipassMaterials[0].alpha = alpha;
	}

	get lineColor()
	{
		return this.multipassMaterials[0].lineColor;
	}

	set lineColor(color)
	{
		this.multipassMaterials[0].lineColor = color;
	}

	get lineThickness()
	{
		return this.multipassMaterials[0].lineThickness;
	}

	set lineThickness(thickness)
	{
		this.multipassMaterials[0].lineThickness = thickness;
	}

	copy(source: BaseLineMaterial)
	{
		super.copy(source);		
		this.lineColor = source.multipassMaterials[0].lineColor;
		this.lineThickness = source.multipassMaterials[0].lineThickness;
		return this;
	}
}

export class DepthmaskLineMaterial extends THREE.MeshBasicMaterial
{
	multipassMaterials: Array<BaseLineMaterial | LineMultipassMaterial>;
	requiresMultipassRendering: boolean;

	constructor()
	{
		super({
			colorWrite: false,
		});

		this.name = "depthMaskLineMat";
		this.requiresMultipassRendering = true;	
		this.multipassMaterials = [new BaseLineMaterial(), new LineMultipassMaterial()];
	}

	get alpha()
	{
		return this.multipassMaterials[1].alpha;
	}
	set alpha(alpha)
	{
		this.multipassMaterials[1].alpha = alpha;
	}

	get lineColor()
	{
		return this.multipassMaterials[1].lineColor;
	}

	set lineColor(color)
	{
		this.multipassMaterials[1].lineColor = color;
	}

	get lineThickness()
	{
		return this.multipassMaterials[1].lineThickness;
	}

	set lineThickness(thickness)
	{
		this.multipassMaterials[1].lineThickness = thickness;
	}

	copy(source: DepthmaskLineMaterial)
	{
		super.copy(source);
		this.lineColor = source.multipassMaterials[1].lineColor;
		this.lineThickness = source.multipassMaterials[1].lineThickness;
		return this;
	}
}

export class CustomMaterials
{
	static standardMat: THREE.MeshStandardMaterial;
	static depthMaskMat: THREE.MeshBasicMaterial;
	static diffuseUnlitMat: THREE.MeshBasicMaterial;
	static videoMat: THREE.MeshBasicMaterial;
	static ghostMat: THREE.ShaderMaterial;
	static partsCatalogGhostedMaterial: THREE.ShaderMaterial;
	static hiddenMat: THREE.MeshBasicMaterial;
	static highlightMat: THREE.MeshBasicMaterial;
	static modelRootMat: THREE.MeshBasicMaterial;
	static textMat: THREE.MeshBasicMaterial;
	static plainWhiteMat: THREE.ShaderMaterial;
	static gridMat: THREE.ShaderMaterial;

	static getStandardMaterial()
	{
		if (!CustomMaterials.standardMat)
		{
			CustomMaterials.standardMat = new THREE.MeshStandardMaterial();
			CustomMaterials.standardMat.name = "standardMat";
			CustomMaterials.standardMat.normalScale = new THREE.Vector2( 1, - 1 );
		}

		return CustomMaterials.standardMat.clone();
	}

	static getDepthMaskMaterial()
	{
		if (!CustomMaterials.depthMaskMat)
		{
			CustomMaterials.depthMaskMat = new THREE.MeshBasicMaterial({
				colorWrite: false
			});
			CustomMaterials.depthMaskMat.name = "depthMaskMat";
			CustomMaterials.depthMaskMat.userData.type = MaterialTypes.DepthMask;
		}

		return CustomMaterials.depthMaskMat.clone();
	}

	static getDepthMaskLineMaterial()
	{
		return new DepthmaskLineMaterial();
	}

	static getDiffuseUnlitMaterial()
	{
		if (!CustomMaterials.diffuseUnlitMat)
		{
			CustomMaterials.diffuseUnlitMat = new THREE.MeshBasicMaterial();
			CustomMaterials.diffuseUnlitMat.name = "diffuseUnlitMat";
		}

		return CustomMaterials.diffuseUnlitMat.clone();
	}

	static getVideoMaterial()
	{
		if (!CustomMaterials.videoMat)
		{
			CustomMaterials.videoMat = new THREE.MeshBasicMaterial();
			CustomMaterials.videoMat.name = "videoMat";
		}


		return CustomMaterials.videoMat.clone();
	}

	static getGhostMaterial()
	{
		if (!CustomMaterials.ghostMat)
		{
			CustomMaterials.ghostMat = new THREE.ShaderMaterial({
				uniforms: {
					u_ghostColor: new THREE.Uniform(new THREE.Color().setRGB(0, 0.9176, 1, THREE.SRGBColorSpace)),
					u_ghostBias: { value: 0.2 },
					u_ghostScale: { value: 1.5 },
					u_ghostPower: { value: 4 },
					u_alphaMultiplier: { value: 1.0 },
				},

				vertexShader: [
					"varying vec3 v_normal;",
					"varying vec3 v_viewPos;",

					THREE.ShaderChunk["morphtarget_pars_vertex"],
					THREE.ShaderChunk["skinning_pars_vertex"],

					"void main()",
					"{",
					THREE.ShaderChunk["skinbase_vertex"],
					THREE.ShaderChunk["begin_vertex"],
					THREE.ShaderChunk["morphtarget_vertex"],
					THREE.ShaderChunk["skinning_vertex"],
					THREE.ShaderChunk["project_vertex"],

					"vec4 worldPosition = modelMatrix * vec4( position, 1.0 );",
					"v_normal = normalize(mat3( modelMatrix) * normal);",
					"v_viewPos = normalize(worldPosition.xyz - cameraPosition);",

					"}"
				].join("\n"),

				fragmentShader: [
					"uniform vec3 u_ghostColor;",
					"uniform float u_ghostBias;",
					"uniform float u_ghostScale;",
					"uniform float u_ghostPower;",
					"uniform float u_alphaMultiplier;",

					"varying vec3 v_normal;",
					"varying vec3 v_viewPos;",

					"void main()",
					"{",
					"	 if(u_alphaMultiplier == 0.0) discard;",
					"    vec4 color = vec4(u_ghostColor,1.0);",
					"    color.a = u_alphaMultiplier * (u_ghostBias + u_ghostScale * pow(1.0 -abs(dot(v_viewPos, v_normal)), u_ghostPower));",
					"    gl_FragColor = color;",
					"    gl_FragColor = linearToOutputTexel( gl_FragColor );",
					"}"
				].join("\n")

			});

			CustomMaterials.ghostMat.transparent = true;
			CustomMaterials.ghostMat.needsUpdate = true;
			CustomMaterials.ghostMat.name = "ghostMat";
		}

		return CustomMaterials.ghostMat;
	}

	static getPartsCatalogGhostedMaterial()
	{
		if(!CustomMaterials.partsCatalogGhostedMaterial)
		{
			CustomMaterials.partsCatalogGhostedMaterial = CustomMaterials.getGhostMaterial().clone();
			CustomMaterials.partsCatalogGhostedMaterial.uniforms["u_ghostColor"].value.setRGB(0.85, 0.85, 0.85).convertSRGBToLinear();
		}

		return CustomMaterials.partsCatalogGhostedMaterial;
	}

	static getBaseLineMaterial()
	{
		return new BaseLineMaterial();
	}

	static getHiddenMaterial()
	{
		if (!CustomMaterials.hiddenMat)
		{
			CustomMaterials.hiddenMat = new THREE.MeshBasicMaterial({
				visible: false
			});
			CustomMaterials.hiddenMat.name = "hiddenMat";
		}

		return CustomMaterials.hiddenMat;
	}

	static getHighlightMaterial()
	{
		if (!CustomMaterials.highlightMat)
		{
			CustomMaterials.highlightMat = new THREE.MeshBasicMaterial({
				wireframe: true,
				color: new THREE.Color('#EEA904').convertSRGBToLinear()
			});
			CustomMaterials.highlightMat.name = "highlight";
		}

		return CustomMaterials.highlightMat.clone();
	}

	static getModelRootMaterial()
	{
		if (!CustomMaterials.modelRootMat)
		{
			CustomMaterials.modelRootMat = new THREE.MeshBasicMaterial({
				visible: false
			});
			CustomMaterials.modelRootMat.name = "modelRoot";
			CustomMaterials.modelRootMat.userData.type = "modelRoot";
		}

		return CustomMaterials.modelRootMat;
	}

	static getTextMaterial()
	{
		if (!CustomMaterials.textMat)
		{
			CustomMaterials.textMat = new THREE.MeshBasicMaterial({
				color: 0xffffff,
			});
			CustomMaterials.textMat.name = "textMat";
			CustomMaterials.textMat.userData.type = "textMat";
		}

		return CustomMaterials.textMat.clone();
	}

	static getPlainWhiteMat()
	{
		if (!CustomMaterials.plainWhiteMat)
		{
			CustomMaterials.plainWhiteMat = new THREE.ShaderMaterial({
				vertexShader: [
					THREE.ShaderChunk["morphtarget_pars_vertex"],
					THREE.ShaderChunk["skinning_pars_vertex"],

					"void main()",
					"{",
					THREE.ShaderChunk["skinbase_vertex"],
					THREE.ShaderChunk["begin_vertex"],
					THREE.ShaderChunk["morphtarget_vertex"],
					THREE.ShaderChunk["skinning_vertex"],
					THREE.ShaderChunk["project_vertex"],
					"}"
				].join("\n"),

				fragmentShader: [
					"void main()",
					"{",
					"    gl_FragColor = vec4(1.0);",
					"}"
				].join("\n")

			});

			CustomMaterials.plainWhiteMat.name = "plainWhiteMat";
		}

		return CustomMaterials.plainWhiteMat;
	}

	static getGridMaterial()
	{
		if (!CustomMaterials.gridMat)
		{
			const texloader = new THREE.TextureLoader();
			const gridTexture = texloader.load('../gridTexture256.png');
			gridTexture.wrapS = THREE.RepeatWrapping;
			gridTexture.wrapT = THREE.RepeatWrapping;
			gridTexture.anisotropy = 4;
			gridTexture.generateMipmaps = true;
			CustomMaterials.gridMat = new THREE.ShaderMaterial({
				uniforms: {
					"texture1": { value: gridTexture },
					"u_worldUnitToMeter": { value: 10.0 },
				},

				vertexShader:
					`varying highp vec2 vUv;
					uniform float u_worldUnitToMeter;
                void main() {
					vUv = vec2(position.x * u_worldUnitToMeter,position.y * u_worldUnitToMeter);
                    vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
                    gl_Position = projectionMatrix * mvPosition;
                }`,

				fragmentShader:
					`uniform sampler2D texture1;
					uniform float u_worldUnitToMeter;
					const float numUnitSubdivisions = 10.0;
					const float levelMultiplier = 4.5;
                varying vec2 vUv;
                    void main() 
					{
						float cameraHeight = abs(cameraPosition.y) * u_worldUnitToMeter;
						float minLevelHeight = 0.0;
						float maxLevelHeight = 5.0;
						vec2 uv = vUv * numUnitSubdivisions;
						// for(int i = 0; i < 4; i++) //i don't know why doing this shader in a loop causes visual artifacts.. i'll blame webgl on that one
						// {
						// 	if(cameraHeight <= maxLevelHeight && cameraHeight >= minLevelHeight)
						// 	{
						// 		gl_FragColor = mix(texture2D(texture1, uv), texture2D(texture1, uv / numUnitSubdivisions),((cameraHeight - minLevelHeight) / (maxLevelHeight - minLevelHeight))) * 0.7;
						// 		return;
						// 	}
						// 	minLevelHeight = maxLevelHeight;
						// 	maxLevelHeight *= levelMultiplier;
						// 	uv = uv / numUnitSubdivisions;
						// }
						if(cameraHeight <= maxLevelHeight && cameraHeight >= minLevelHeight)
						{
							gl_FragColor = mix(texture2D(texture1, uv), texture2D(texture1, uv / numUnitSubdivisions),((cameraHeight - minLevelHeight) / (maxLevelHeight - minLevelHeight))) * 0.7;
							return;
						}
						uv = uv / numUnitSubdivisions;
						
						minLevelHeight = maxLevelHeight;
						maxLevelHeight *= levelMultiplier;
						if(cameraHeight <= maxLevelHeight && cameraHeight >= minLevelHeight)
						{
							gl_FragColor = mix(texture2D(texture1, uv), texture2D(texture1, uv / numUnitSubdivisions),((cameraHeight - minLevelHeight) / (maxLevelHeight - minLevelHeight))) * 0.7;
							return;
						}
						uv = uv / numUnitSubdivisions;
						
						minLevelHeight = maxLevelHeight;
						maxLevelHeight *= levelMultiplier;
						if(cameraHeight <= maxLevelHeight && cameraHeight >= minLevelHeight)
						{
							gl_FragColor = mix(texture2D(texture1, uv), texture2D(texture1, uv / numUnitSubdivisions),((cameraHeight - minLevelHeight) / (maxLevelHeight - minLevelHeight))) * 0.7;
							return;
						}
						uv = uv / numUnitSubdivisions;
						
						minLevelHeight = maxLevelHeight;
						maxLevelHeight *= levelMultiplier;
						if(cameraHeight <= maxLevelHeight && cameraHeight >= minLevelHeight)
						{
							gl_FragColor = mix(texture2D(texture1, uv), texture2D(texture1, uv / numUnitSubdivisions),((cameraHeight - minLevelHeight) / (maxLevelHeight - minLevelHeight))) * 0.7;
							return;
						}
						uv = uv / numUnitSubdivisions;
						
                    }`

			});
			CustomMaterials.gridMat.side = THREE.DoubleSide;
			CustomMaterials.gridMat.transparent = true;
			CustomMaterials.gridMat.needsUpdate = true;
			CustomMaterials.gridMat.depthWrite = false;
			CustomMaterials.gridMat.name = "gridMat";
		}

		return CustomMaterials.gridMat;
	}
}


